import { Ref, useCallback, useImperativeHandle, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  AddLocation,
  AddLocationTab,
  DEFAULT_LOCATION_TAB,
} from "@coworker/apprestructured/src/task-management/components/AddLocation/AddLocation";
import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useFilteredSalesLocations } from "@coworker/apprestructured/src/task-management/services/useSalesLocations";
import {
  LocationChange,
  Location,
} from "@coworker/apprestructured/src/task-management/types/SalesLocation";

export type AddLocationCreatorFragmentConfigProps = {
  field: string;
};

type AddLocationCreatorFragmentProps = {
  configProps: AddLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AddLocationCreatorFragment({
  configProps,
  commonProps,
  ref,
}: AddLocationCreatorFragmentProps) {
  const { field } = configProps;
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;

  const location = validateInitialDetailsFieldValue<Location>(
    getByPath(task, field),
    field,
    taskType
  );

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const handleConfirmClick = useCallback(
    ({ slid, customLocation }: LocationChange) => {
      if (slid !== undefined) {
        onTaskChange(setByPath("details.location.slid", slid));
      }
      if (customLocation !== undefined) {
        onTaskChange(setByPath("details.location.custom", customLocation));
      }
    },
    [field, onTaskChange]
  );

  const [activeAddLocationTab, setActiveAddLocationTab] =
    useState<AddLocationTab>(DEFAULT_LOCATION_TAB);

  const handleTabChanged = useCallback((tabId: AddLocationTab) => {
    setActiveAddLocationTab(tabId);
  }, []);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const {
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
  } = useFilteredSalesLocations(
    activeAddLocationTab === "locations" ? itemNoAndType : null,
    searchQuery
  );

  const valid = !isLoading(location) && location !== null;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(location) || isLoading(itemNoAndType)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <AddLocation
      slid={location?.slid ?? ""}
      customLocation={location?.custom ?? ""}
      onTabChanged={handleTabChanged}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filteredLocations={filteredLocations}
      totalAssignedSalesSpaceQuantity={totalAssignedSalesSpaceQuantity}
      isLoadingLocations={isLoadingLocations}
      itemNoAndType={itemNoAndType}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      isError={!location && showValidation}
    />
  );
}

export default AddLocationCreatorFragment;
